import { ensureRouteData } from '@/app/query-client'
import { brokerListQueryOptions } from '@/features/brokers/api/queries'
import type { RouterContext } from '@/routes/layouts/root'
import { allBrokersSearchSchema, toBrokerListQuery } from '@/screens/brokers/search'

/**
 * The `/brokers/all` loader.
 *
 * MAIN: the broker list, keyed on the WHOLE search object (`q`/`sort`/`page`/
 * `pageSize`) — the table and its pager are the entire page below the
 * search/sort row, so a first-load failure blanks all of it.
 *
 * Because this main data is keyed on search params, the screen does NOT read
 * it with a suspense query — `useBrokerList` stays plain `useQuery` +
 * `keepPreviousData` — see docs/route-loading.md, "Search params". This
 * loader only makes sure the ENTRY url's query is in the cache before the
 * page mounts; a later search/sort/page change refetches through the
 * screen's own hook, with no route-level pending skeleton.
 *
 * Its own module so a test can mount it under a root of its own — see
 * OverviewScreen.loader.ts for the fuller version of this note.
 *
 * Goes through `toBrokerListQuery` rather than handing `search` to
 * `brokerListQueryOptions` as-is: `allBrokersSearchSchema` and
 * `BrokerListQuery` are the same type, so that used to look like a no-op, but
 * a URL param the schema does not declare (a `?utm_source=`) survives
 * `Route.useSearch()` on the screen side even though this loader's own
 * `.parse()` above already dropped it — see the picker's own comment in
 * `search.ts`. Going through the same picker here keeps this loader's key
 * and the screen's `useBrokerList` key provably identical instead of
 * identical by coincidence.
 */
export async function loadAllBrokers({
  context: { queryClient },
  location,
}: {
  context: RouterContext
  location: { search: unknown }
}): Promise<void> {
  const search = allBrokersSearchSchema.parse(location.search)
  await ensureRouteData(queryClient, brokerListQueryOptions(toBrokerListQuery(search)))
}
